/**
 * Complete replacement for date-fns library
 * This utility provides all date-fns functions used in the project
 * using native JavaScript Date methods
 */

/**
 * Format a date according to the specified format string
 * Supports common date-fns format tokens
 */
export const format = (date, formatStr) => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return '';
  }

  const d = new Date(date);
  const year = d.getFullYear();
  const monthIndex = d.getMonth(); // 0-11
  const month = monthIndex + 1; // 1-12 for numeric formats
  const day = d.getDate();
  const hours = d.getHours();
  const minutes = d.getMinutes();
  const seconds = d.getSeconds();
  const milliseconds = d.getMilliseconds();
  const dayOfWeek = d.getDay();

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const monthNamesShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const dayNamesShort = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  let result = formatStr;
  
  // Replace AM/PM patterns first with unique placeholders to avoid conflicts with month names
  const amPmValue = hours >= 12 ? 'PM' : 'AM';
  const amPmPlaceholder = '__AMPM__';
  const amPmPlaceholderA = '__AMPM_A__';
  
  // Replace 'a' and 'A' with placeholders
  result = result.replace(/a/g, amPmPlaceholder);
  result = result.replace(/A/g, amPmPlaceholderA);
  
  // Year
  result = result.replace(/yyyy/g, year);
  result = result.replace(/yy/g, String(year).slice(-2));
  
  // Month - use monthIndex (0-11) directly for month names
  result = result.replace(/MMMM/g, monthNames[monthIndex]);
  result = result.replace(/MMM/g, monthNamesShort[monthIndex]);
  result = result.replace(/MM/g, String(month).padStart(2, '0'));
  result = result.replace(/M/g, String(month));
  
  // Day
  result = result.replace(/dd/g, String(day).padStart(2, '0'));
  result = result.replace(/d/g, String(day));
  
  // Day of week
  result = result.replace(/EEEE/g, dayNames[dayOfWeek]);
  result = result.replace(/EEE/g, dayNamesShort[dayOfWeek]);
  
  // Hours
  result = result.replace(/HH/g, String(hours).padStart(2, '0'));
  result = result.replace(/H/g, String(hours));
  result = result.replace(/hh/g, String(hours % 12 || 12).padStart(2, '0'));
  result = result.replace(/h/g, String(hours % 12 || 12));
  
  // Minutes
  result = result.replace(/mm/g, String(minutes).padStart(2, '0'));
  result = result.replace(/m/g, String(minutes));
  
  // Seconds
  result = result.replace(/ss/g, String(seconds).padStart(2, '0'));
  result = result.replace(/s/g, String(seconds));
  
  // Milliseconds
  result = result.replace(/SSS/g, String(milliseconds).padStart(3, '0'));
  
  // Replace placeholders back with AM/PM values
  result = result.replace(new RegExp(amPmPlaceholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), amPmValue);
  result = result.replace(new RegExp(amPmPlaceholderA.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), amPmValue);
  
  // Pretty format (PPpp) - "Jan 01, 2024, 12:00 AM"
  if (formatStr === 'PPpp') {
    return format(d, 'MMM dd, yyyy, hh:mm a');
  }
  
  // Pretty format (PP) - "Jan 01, 2024"
  if (formatStr === 'PP') {
    return format(d, 'MMM dd, yyyy');
  }

  return result;
};

/**
 * Add days to a date
 */
export const addDays = (date, amount) => {
  const result = new Date(date);
  result.setDate(result.getDate() + amount);
  return result;
};

/**
 * Add months to a date
 */
export const addMonths = (date, amount) => {
  const result = new Date(date);
  result.setMonth(result.getMonth() + amount);
  return result;
};

/**
 * Add years to a date
 */
export const addYears = (date, amount) => {
  const result = new Date(date);
  result.setFullYear(result.getFullYear() + amount);
  return result;
};

/**
 * Subtract days from a date
 */
export const subDays = (date, amount) => {
  return addDays(date, -amount);
};

/**
 * Subtract months from a date
 */
export const subMonths = (date, amount) => {
  return addMonths(date, -amount);
};

/**
 * Subtract years from a date
 */
export const subYears = (date, amount) => {
  return addYears(date, -amount);
};

/**
 * Subtract quarters from a date
 */
export const subQuarters = (date, amount) => {
  return addMonths(date, -amount * 3);
};

/**
 * Get the start of a day (00:00:00.000)
 */
export const startOfDay = (date) => {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
};

/**
 * Get the end of a day (23:59:59.999)
 */
export const endOfDay = (date) => {
  const result = new Date(date);
  result.setHours(23, 59, 59, 999);
  return result;
};

/**
 * Get the start of a week (Sunday)
 */
export const startOfWeek = (date, options = {}) => {
  const result = new Date(date);
  const weekStartsOn = options.weekStartsOn !== undefined ? options.weekStartsOn : 0; // 0 = Sunday
  const day = result.getDay();
  const diff = (day < weekStartsOn ? 7 : 0) + day - weekStartsOn;
  result.setDate(result.getDate() - diff);
  result.setHours(0, 0, 0, 0);
  return result;
};

/**
 * Get the end of a week (Saturday)
 */
export const endOfWeek = (date, options = {}) => {
  const result = new Date(date);
  const weekStartsOn = options.weekStartsOn !== undefined ? options.weekStartsOn : 0; // 0 = Sunday
  const day = result.getDay();
  const diff = (day < weekStartsOn ? -7 : 0) - day + weekStartsOn + 6;
  result.setDate(result.getDate() + diff);
  result.setHours(23, 59, 59, 999);
  return result;
};

/**
 * Get the start of a month
 */
export const startOfMonth = (date) => {
  const result = new Date(date);
  result.setDate(1);
  result.setHours(0, 0, 0, 0);
  return result;
};

/**
 * Get the end of a month
 */
export const endOfMonth = (date) => {
  const result = new Date(date);
  result.setMonth(result.getMonth() + 1, 0);
  result.setHours(23, 59, 59, 999);
  return result;
};

/**
 * Get the start of a quarter
 */
export const startOfQuarter = (date) => {
  const result = new Date(date);
  const quarter = Math.floor(result.getMonth() / 3);
  result.setMonth(quarter * 3, 1);
  result.setHours(0, 0, 0, 0);
  return result;
};

/**
 * Get the end of a quarter
 */
export const endOfQuarter = (date) => {
  const result = new Date(date);
  const quarter = Math.floor(result.getMonth() / 3);
  result.setMonth(quarter * 3 + 3, 0);
  result.setHours(23, 59, 59, 999);
  return result;
};

/**
 * Get the start of a year
 */
export const startOfYear = (date) => {
  const result = new Date(date);
  result.setMonth(0, 1);
  result.setHours(0, 0, 0, 0);
  return result;
};

/**
 * Get the end of a year
 */
export const endOfYear = (date) => {
  const result = new Date(date);
  result.setMonth(11, 31);
  result.setHours(23, 59, 59, 999);
  return result;
};

/**
 * Calculate the difference in days between two dates
 */
export const differenceInDays = (dateLeft, dateRight) => {
  const left = startOfDay(dateLeft);
  const right = startOfDay(dateRight);
  const diffTime = left.getTime() - right.getTime();
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Calculate the difference in months between two dates
 */
export const differenceInMonths = (dateLeft, dateRight) => {
  const left = new Date(dateLeft);
  const right = new Date(dateRight);
  const yearsDiff = left.getFullYear() - right.getFullYear();
  const monthsDiff = left.getMonth() - right.getMonth();
  return yearsDiff * 12 + monthsDiff;
};

/**
 * Check if the first date is after the second date
 */
export const isAfter = (date, dateToCompare) => {
  return date.getTime() > dateToCompare.getTime();
};

/**
 * Check if the first date is before the second date
 */
export const isBefore = (date, dateToCompare) => {
  return date.getTime() < dateToCompare.getTime();
};

/**
 * Check if two dates are the same day
 */
export const isSameDay = (dateLeft, dateRight) => {
  const left = startOfDay(dateLeft);
  const right = startOfDay(dateRight);
  return left.getTime() === right.getTime();
};

/**
 * Parse an ISO 8601 date string
 */
export const parseISO = (dateString) => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    throw new Error(`Invalid date: ${dateString}`);
  }
  return date;
};

/**
 * Locale object for enUS (used by react-date-range)
 * This is a simplified version that matches what react-date-range expects
 */
export const enUS = {
  localize: {
    month: (n) => {
      const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      return months[n];
    },
    day: (n) => {
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      return days[n];
    },
  },
  formatLong: {
    date: () => 'MMMM d, yyyy',
    time: () => 'h:mm a',
    dateTime: () => 'MMMM d, yyyy h:mm a',
  },
  match: {
    ordinalNumber: () => null,
    era: () => null,
    quarter: () => null,
    month: () => null,
    day: () => null,
    dayPeriod: () => null,
  },
  options: {
    weekStartsOn: 0, // Sunday
    firstWeekContainsDate: 1,
  },
};

// Export all functions as default object for convenience
export default {
  format,
  addDays,
  addMonths,
  addYears,
  subDays,
  subMonths,
  subYears,
  subQuarters,
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  endOfQuarter,
  startOfYear,
  endOfYear,
  differenceInDays,
  differenceInMonths,
  isAfter,
  isBefore,
  isSameDay,
  parseISO,
  enUS,
};
